/**
 * resource:action の権限定義と初期ロールの付与表（02 4.1・4.2）。人間レビュー必須（02 11.1 No.1）。
 * 4.1 の表と PERMISSION_DEFINITIONS（permission-definitions.ts。設計書の表から生成）の一致は test/permissions.test.ts で確かめる。
 */
import {
  ACTION_TYPE_NAMES,
  PERMISSION_CODES,
  PERMISSION_DEFINITIONS,
  RESOURCE_TYPE_NAMES,
} from './permission-definitions';
import type { PermissionCode, PermissionDefinition } from './permission-definitions';

export { ACTION_TYPE_NAMES, PERMISSION_CODES, PERMISSION_DEFINITIONS, RESOURCE_TYPE_NAMES };
export type { PermissionCode, PermissionDefinition };

export const ADMIN_ROLE_CODE = 'admin';
export const USER_ROLE_CODE = 'user';

const PERMISSION_CODE_SET: ReadonlySet<string> = new Set(PERMISSION_CODES);
const ADMIN_FIXED_SET: ReadonlySet<string> = new Set(
  PERMISSION_DEFINITIONS.filter((p) => p.is_admin_fixed).map((p) => p.permission_code),
);

/** 管理者（admin）に付ける権限：4.1 の全 130 件（固定。変更できない：D-026） */
export const ADMIN_ROLE_PERMISSION_CODES: readonly PermissionCode[] = PERMISSION_DEFINITIONS.map(
  (p) => p.permission_code,
);

/** 一般ユーザー（user）に付ける権限：4.1 の「一般ユーザー」が ○ の 59 件 */
export const USER_ROLE_PERMISSION_CODES: readonly PermissionCode[] = PERMISSION_DEFINITIONS.filter(
  (p) => p.is_user_granted,
).map((p) => p.permission_code);

/** seed で入れる 2 ロール（02 4.2） */
export const INITIAL_ROLE_DEFINITIONS = [
  {
    role_code: ADMIN_ROLE_CODE,
    role_name: '管理者',
    description: 'すべての操作ができる',
    display_order: 1,
    is_system_role: true,
    role_category_type: 'system',
    permission_codes: ADMIN_ROLE_PERMISSION_CODES,
  },
  {
    role_code: USER_ROLE_CODE,
    role_name: '一般ユーザー',
    description: '登録・参照・変更ができる（削除は活動履歴のみ。ファイル出力・設定変更・監査ログは不可）',
    display_order: 2,
    is_system_role: true,
    role_category_type: 'system',
    permission_codes: USER_ROLE_PERMISSION_CODES,
  },
] as const;

export function isPermissionCode(code: string): code is PermissionCode {
  return PERMISSION_CODE_SET.has(code);
}

/** 管理者固定（admin 以外のロールに付けられない：D-025）か。274（10/14）から該当なし（旧と同じくどのロールにも全権限を付けられる） */
export function isAdminFixedPermission(code: string): boolean {
  return ADMIN_FIXED_SET.has(code);
}

export function splitPermissionCode(code: string): { resource_type: string; action_type: string } {
  const [resource_type = '', action_type = ''] = code.split(':');
  return { resource_type, action_type };
}

/** 権限カタログ（API-USER-16 の応答。DB を読まない） */
export function buildPermissionCatalog(): Array<{
  permission_code: PermissionCode;
  resource_type: string;
  action_type: string;
  resource_name: string;
  action_name: string;
  is_admin_fixed: boolean;
}> {
  return PERMISSION_DEFINITIONS.map((p) => ({
    permission_code: p.permission_code,
    resource_type: p.resource_type,
    action_type: p.action_type,
    resource_name: RESOURCE_TYPE_NAMES[p.resource_type] ?? p.resource_type,
    action_name: ACTION_TYPE_NAMES[p.action_type] ?? p.action_type,
    is_admin_fixed: p.is_admin_fixed,
  }));
}
